import React, { useState, useEffect } from 'react';
import { X, Copy, CheckCircle, Loader, QrCode } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PixPaymentModal = ({ isOpen, onClose, paymentData, onSuccess }) => {
  const [qrCode, setQrCode] = useState('');
  const [qrCodeBase64, setQrCodeBase64] = useState('');
  const [paymentId, setPaymentId] = useState('');
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState('pending'); // pending, checking, approved, error
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && paymentData) {
      createPayment();
    }
  }, [isOpen, paymentData]);

  useEffect(() => {
    if (status === 'checking' && paymentId) {
      // Start polling for payment status
      const interval = setInterval(() => {
        checkPaymentStatus();
      }, 3000); // Check every 3 seconds

      return () => clearInterval(interval);
    }
  }, [status, paymentId]);

  const createPayment = async () => {
    try {
      setStatus('creating');
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API}/mercadopago/create-subscription-payment`,
        {
          restaurant_id: paymentData.restaurant_id,
          months: paymentData.months || 1
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setQrCode(response.data.qr_code);
        setQrCodeBase64(response.data.qr_code_base64);
        setPaymentId(response.data.payment_id);
        setStatus('checking');
      } else {
        setError('Erro ao criar pagamento');
        setStatus('error');
      }
    } catch (err) {
      console.error('Error creating payment:', err);
      setError(err.response?.data?.detail || 'Erro ao criar pagamento PIX');
      setStatus('error');
    }
  };

  const checkPaymentStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/mercadopago/subscription-status/${paymentId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.status === 'approved') {
        setStatus('approved');
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
    }
  };

  const copyToClipboard = () => {
    if (qrCode) {
      navigator.clipboard.writeText(qrCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-gray-900 rounded-2xl border border-gray-700 shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-white">Pagamento PIX</h2>
            <p className="text-gray-400 text-sm mt-1">
              {paymentData?.months === 1 ? '1 mês' : `${paymentData?.months} meses`} - R$ {(paymentData?.amount || 99).toFixed(2)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {status === 'creating' && (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader className="w-12 h-12 text-emerald-500 animate-spin mb-4" />
              <p className="text-white font-semibold">Gerando pagamento PIX...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-6 h-6 text-red-400" />
              </div>
              <p className="text-red-400 font-semibold mb-2">Erro ao criar pagamento</p>
              <p className="text-gray-400 text-sm">{error}</p>
              <button
                onClick={createPayment}
                className="mt-4 px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
              >
                Tentar Novamente
              </button>
            </div>
          )}

          {(status === 'checking' || status === 'approved') && (
            <>
              {/* QR Code */}
              {qrCodeBase64 && (
                <div className="flex flex-col items-center">
                  <div className="bg-white p-4 rounded-2xl shadow-xl">
                    <img
                      src={`data:image/png;base64,${qrCodeBase64}`}
                      alt="QR Code PIX"
                      className="w-64 h-64"
                    />
                  </div>
                  <p className="text-gray-400 text-sm mt-4 text-center">
                    Escaneie o QR Code com seu aplicativo bancário
                  </p>
                </div>
              )}

              {/* PIX Code */}
              {qrCode && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-400">
                    Ou copie o código PIX:
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={qrCode}
                      readOnly
                      className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white text-sm font-mono"
                    />
                    <button
                      onClick={copyToClipboard}
                      className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors flex items-center gap-2"
                    >
                      {copied ? (
                        <>
                          <CheckCircle className="w-5 h-5" />
                          Copiado!
                        </>
                      ) : (
                        <>
                          <Copy className="w-5 h-5" />
                          Copiar
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Status */}
              {status === 'checking' && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6 text-center">
                  <div className="flex items-center justify-center mb-4">
                    <Loader className="w-8 h-8 text-emerald-400 animate-spin" />
                  </div>
                  <p className="text-white font-semibold mb-2">Aguardando pagamento...</p>
                  <p className="text-gray-400 text-sm">
                    Estamos verificando seu pagamento automaticamente
                  </p>
                </div>
              )}

              {status === 'approved' && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 text-center">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-10 h-10 text-green-400" />
                  </div>
                  <p className="text-green-400 font-bold text-xl mb-2">Pagamento Aprovado!</p>
                  <p className="text-gray-400 text-sm">
                    Seu plano foi renovado com sucesso
                  </p>
                </div>
              )}

              {/* Instructions */}
              <div className="bg-gray-800/50 rounded-xl p-4 space-y-2">
                <h3 className="text-white font-semibold text-sm mb-3">Como pagar:</h3>
                <div className="space-y-2 text-gray-400 text-sm">
                  <p>1. Abra seu aplicativo bancário</p>
                  <p>2. Escolha pagar com PIX</p>
                  <p>3. Escaneie o QR Code ou cole o código</p>
                  <p>4. Confirme o pagamento</p>
                  <p className="text-green-400 font-semibold mt-3">
                    ✓ O pagamento será confirmado automaticamente
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default PixPaymentModal;
